import { useState } from 'react'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { usePricingMutations, pricingKeys } from '@/hooks/usePricing'
import { fetchPricingRates, upsertPricingRate } from '@/lib/api/pricing'
import type { PolicyVersion } from '@/types/pricing'
import { useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'

interface VersionCreateDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  policyId: string
  versions: PolicyVersion[]
}

export function VersionCreateDialog({
  open,
  onOpenChange,
  policyId,
  versions,
}: VersionCreateDialogProps) {
  const mutations = usePricingMutations()
  const queryClient = useQueryClient()

  const maxVersion = versions.reduce((max, v) => Math.max(max, v.versionNumber), 0)
  const activeVersion = versions.find((v) => v.status === 'active')

  const [effectiveFrom, setEffectiveFrom] = useState(
    new Date().toISOString().split('T')[0]
  )
  const [effectiveTo, setEffectiveTo] = useState('')
  const [label, setLabel] = useState('')
  const [cloneFromId, setCloneFromId] = useState(activeVersion?.id ?? '')
  const [isCloning, setIsCloning] = useState(false)

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    const nextVersion = maxVersion + 1

    try {
      const newVersionRow = await new Promise<{ id: string }>((resolve, reject) => {
        mutations.createVersion.mutate(
          {
            policyId,
            versionNumber: nextVersion,
            effectiveFrom,
            effectiveTo: effectiveTo || undefined,
            label: label || undefined,
          },
          {
            onSuccess: (data) => resolve({ id: (data as Record<string, string>).id }),
            onError: reject,
          }
        )
      })

      // Clone rates if a source version was selected
      if (cloneFromId) {
        setIsCloning(true)
        const sourceRates = await fetchPricingRates(cloneFromId)
        for (const rate of sourceRates) {
          await upsertPricingRate({
            version_id: newVersionRow.id,
            catalog_item_id: rate.catalog_item_id,
            pricing_category_id: rate.pricing_category_id,
            customer_type: rate.customer_type,
            volume_min_gallons: rate.volume_min_gallons,
            volume_max_gallons: rate.volume_max_gallons,
            unit_id: rate.unit_id,
            price: rate.price,
            cost_per_unit: rate.cost_per_unit,
            waiver_min_fuel_gallons: rate.waiver_min_fuel_gallons,
            includes_towing: rate.includes_towing,
            contract_fuel_provider: rate.contract_fuel_provider,
            contract_fuel_notes: rate.contract_fuel_notes,
            weekly_rate: rate.weekly_rate,
            monthly_rate: rate.monthly_rate,
            notes: rate.notes,
            sort_order: rate.sort_order,
          })
        }
        setIsCloning(false)
        if (sourceRates.length > 0) {
          toast.success(`Cloned ${sourceRates.length} rates`)
        }
      }

      queryClient.invalidateQueries({ queryKey: pricingKeys.versions(policyId) })
      onOpenChange(false)
      resetForm()
    } catch {
      // errors handled by mutation onError
    }
  }

  const resetForm = () => {
    setEffectiveFrom(new Date().toISOString().split('T')[0])
    setEffectiveTo('')
    setLabel('')
    setCloneFromId(activeVersion?.id ?? '')
    setIsCloning(false)
  }

  const isPending = mutations.createVersion.isPending || isCloning

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <form onSubmit={handleSubmit}>
          <DialogHeader>
            <DialogTitle>Create New Version</DialogTitle>
            <DialogDescription>
              Version {maxVersion + 1} will be created as a draft.
            </DialogDescription>
          </DialogHeader>

          <div className="grid gap-4 py-4">
            <div className="grid gap-2">
              <Label>Effective From</Label>
              <Input
                type="date"
                value={effectiveFrom}
                onChange={(e) => setEffectiveFrom(e.target.value)}
                required
              />
            </div>

            <div className="grid gap-2">
              <Label>Effective To (optional)</Label>
              <Input
                type="date"
                value={effectiveTo}
                onChange={(e) => setEffectiveTo(e.target.value)}
              />
            </div>

            <div className="grid gap-2">
              <Label>Label (optional)</Label>
              <Input
                value={label}
                onChange={(e) => setLabel(e.target.value)}
                placeholder="e.g., Summer 2026, Q3 Update"
              />
            </div>

            {versions.length > 0 && (
              <div className="grid gap-2">
                <Label>Clone Rates From</Label>
                <Select value={cloneFromId} onValueChange={setCloneFromId}>
                  <SelectTrigger>
                    <SelectValue placeholder="Don't clone (empty)" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">Don&apos;t clone</SelectItem>
                    {versions.map((v) => (
                      <SelectItem key={v.id} value={v.id}>
                        v{v.versionNumber}
                        {v.label ? ` — ${v.label}` : ''}
                        {v.status === 'active' ? ' (active)' : ''}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <p className="text-xs text-muted-foreground">
                  Copy all rates from an existing version into the new one.
                </p>
              </div>
            )}
          </div>

          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
              Cancel
            </Button>
            <Button type="submit" disabled={isPending || !effectiveFrom}>
              {isCloning ? 'Cloning rates...' : isPending ? 'Creating...' : 'Create Version'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  )
}
